import { types, flow } from 'mobx-state-tree';
import moment from 'moment';
import { toJS } from 'mobx';
import {
  convertFromRaw,
  EditorState,
  SelectionState,
  Modifier,
} from 'draft-js';
import Router from 'next/router';
import Immutable from 'immutable';
import {
  getPages,
  getPageById,
  postPageUpdate,
  postNewPage,
  deletePageById,
  postPageRole,
  deletePageRole,
} from '../api/pages';
import { getTags } from '../api/tags';
import { handleResponse } from '../api';
import { getStore } from './index';
import { preparePageForSave, getSelectionForEntity } from './utils';

const getAllEntitiesFromContentState = contentState => {
  let entities = Immutable.List();
  contentState.getBlockMap().forEach(block => {
    block.findEntityRanges(character => {
      if (character.getEntity() === null) return;
      entities = entities.push(contentState.getEntity(character.getEntity()));
    });
  });
  return entities;
};

const Page = types
  .model('Page', {
    entityMap: types.optional(types.map(types.frozen()), {}),
    data: types.optional(types.frozen(), {}),
    blockOrder: types.optional(types.map(types.number), {}),
    blocks: types.optional(types.array(types.frozen()), []),
    viewAccess: types.optional(types.array(types.frozen()), []),
    createdAt: types.maybeNull(types.string),
    createdByUser: types.maybeNull(types.string),
    createdByUserName: types.maybeNull(types.string),
    state: types.string,
    tags: types.optional(types.array(types.string), []),
    title: types.string,
    type: types.string,
    updatedAt: types.maybeNull(types.string),
    publishedAt: types.maybeNull(types.string),
    uuid: types.string,
    editorState: types.optional(types.frozen(), null),
    dataHasChanges: types.optional(types.boolean, false),
    editorHasChanges: types.optional(types.boolean, false),
    lastChangedEditorState: types.optional(types.frozen(), null),
    isSaving: types.optional(types.boolean, false),
  })
  .actions(self => {
    const afterCreate = () => {
      const { blocks, entityMap } = self.toJSON();
      if (blocks.length > 0 && entityMap) {
        const contentState = convertFromRaw({ blocks, entityMap });
        self.editorState = EditorState.createWithContent(contentState);
      }
    };
    const setState = state => {
      self.state = state;
      self.savePage();
    };
    const addRole = role => {
      self.viewAccess = [...self.viewAccess, role];
      postPageRole(self.uuid, role);
    };
    const removeRole = role => {
      self.viewAccess = self.viewAccess.filter(r => r !== role);
      deletePageRole(self.uuid, role);
    };
    const setTitle = title => {
      self.title = title;
      self.dataHasChanges = true;
    };
    const setTags = tags => {
      self.tags = tags;
      self.dataHasChanges = true;
    };
    const updatePageData = data => {
      self.data = {
        ...self.data,
        ...data,
      };
      self.dataHasChanges = true;
    };
    const update = page => {
      Object.keys(page).forEach(key => {
        if (self.hasOwnProperty(key)) self[key] = page[key];
      });
      const { blocks, entityMap } = self.toJSON();
      if (blocks.length > 0 && entityMap) {
        const contentState = convertFromRaw({ blocks, entityMap });
        self.editorState = EditorState.createWithContent(contentState);
      }

    };
    const updateEntityDataNew = (entityKey, data) => {
      const newContentState = self.editorState
        .getCurrentContent()
        .mergeEntityData(entityKey, data);

      const newEditorState = EditorState.forceSelection(
        EditorState.push(self.editorState, newContentState, 'apply-entity'),
        getSelectionForEntity(self.editorState, entityKey, true),
      );
      self.setEditorState(newEditorState);
    };
    const deleteEntity = entityKey => {
      const selection = getSelectionForEntity(self.editorState, entityKey);
      const newContentState = Modifier.applyEntity(
        self.editorState.getCurrentContent(),
        selection,
        null,
      );
      const newEditorState = EditorState.push(
        self.editorState,
        newContentState,
        'apply-entity',
      );
      // self.dataHasChanges = true;
      self.setEditorState(newEditorState);
    };
    const deleteBlock = block => {
      const blockKey = block.getKey();
      const contentState = self.editorState.getCurrentContent();
      const keyBefore = contentState.getKeyBefore(blockKey);
      const blockBefore = contentState.getBlockForKey(keyBefore);
      // create a selection from the end of the block before,
      // to the end of the provided block,
      // TODO: this might break if there is no block before
      let selectionForRemoval = SelectionState.createEmpty(keyBefore).merge({
        anchorOffset: blockBefore !== undefined ? blockBefore.getLength() : 0,
        focusOffset: 0,
        focusKey: blockKey,
      });
      const keyAfter = contentState.getKeyAfter(blockKey);
      const blockAfter = contentState.getBlockForKey(keyAfter);
      // if the following block after the block we are removing is a caption,
      // expand the selection to this block, to remove it aswell
      if (blockAfter && blockAfter.getType() === 'caption') {
        selectionForRemoval = selectionForRemoval.merge({
          focusOffset: blockAfter.getLength(),
          focusKey: keyAfter,
        });
      }
      const newContentState = Modifier.removeRange(
        contentState,
        selectionForRemoval,
        'backward',
      );
      const newEditorState = EditorState.push(
        self.editorState,
        newContentState,
        'remove-range',
      );
      self.setEditorState(newEditorState);
    };
    const updateBlockData = (block, data) => {
      const contentState = self.editorState.getCurrentContent();
      let newContentState = Modifier.mergeBlockData(
        contentState,
        SelectionState.createEmpty(block.getKey()),
        data,
      );
      const blockAfter = contentState.getBlockAfter(block.getKey());
      if (blockAfter && blockAfter.getType() === 'caption') {
        newContentState = Modifier.mergeBlockData(
          newContentState,
          SelectionState.createEmpty(blockAfter.getKey()),
          data,
        );
      }
      const newEditorState = EditorState.push(
        self.editorState,
        newContentState,
        'change-block-data',
      );
      self.setEditorState(newEditorState);
    };
    const setEditorState = editorState => {
      const lastContentState = self.lastChangedEditorState
        ? self.lastChangedEditorState.getCurrentContent()
        : self.editorState.getCurrentContent();
      const newContentState = editorState.getCurrentContent();
      const lastEntities = getAllEntitiesFromContentState(lastContentState);
      const newEntities = getAllEntitiesFromContentState(newContentState);
      const blockMapSame =
        lastContentState.getBlockMap() === newContentState.getBlockMap();
      const entitiesSame =
        JSON.stringify(lastEntities.toJS()) ===
        JSON.stringify(newEntities.toJS());
      const editorHasChanges = !(blockMapSame && entitiesSame);
      if (self.editorHasChanges !== editorHasChanges) {
        self.editorHasChanges = editorHasChanges;
        self.lastChangedEditorState = self.editorState;
      }
      self.editorState = editorState;
    };
    const updateEntityData = (blockKey, entityKey, entityData) => {
      let newContentState = self.editorState
        .getCurrentContent()
        .mergeEntityData(entityKey, entityData);

      const selection = getSelectionForEntity(self.editorState, entityKey);
      // const selection = SelectionState.createEmpty(blockKey).set(
      //   'focusOffset',
      //   1,
      // );
      newContentState = Modifier.applyEntity(
        newContentState,
        selection,
        entityKey,
      );
      const newEditorState = EditorState.push(
        self.editorState,
        newContentState,
        'apply-entity',
      );
      self.setEditorState(newEditorState);
      self.dataHasChanges = true;
      return newEditorState;
    };
    const saveNewPage = flow(function*() {
      self.isSaving = true;
      const payload = preparePageForSave(self);
      delete payload['uuid'];
      const response = yield postNewPage(payload);
      handleResponse([
        [
          200,
          () => {
            Object.keys(response.data).forEach(key => {
              self[key] = response.data[key];
            });
            self.editorHasChanges = false;
            self.dataHasChanges = false;
            Router.replace('/admin/p/[uuid]', `/admin/p/${response.data.uuid}`);
          },
        ],
        [
          'default',
          () => {
            console.error('error creating page', response.data);
          },
        ],
      ])(response);
      self.isSaving = false;
      return response;
    });

    const savePage = flow(function*() {
      self.isSaving = true;
      const payload = preparePageForSave(self);
      const response = yield postPageUpdate(self.uuid, payload);
      handleResponse([
        [
          200,
          () => {
            Object.keys(response.data).forEach(key => {
              self[key] = response.data[key];
            });
            self.editorHasChanges = false;
            self.dataHasChanges = false;
          },
        ],
        [
          'default',
          () => {
            console.log('error saving page', response.data);
          },
        ],
      ])(response);
      self.isSaving = false;
      return response;
    });
    return {
      addRole,
      removeRole,
      saveNewPage,
      deleteEntity,
      setTags,
      updateBlockData,
      afterCreate,
      updateEntityDataNew,
      updateEntityData,
      setEditorState,
      deleteBlock,
      update,
      updatePageData,
      savePage,
      setTitle,
      setState,
    };
  })
  .views(self => ({
    get parsed() {
      return {
        ...self.toJSON(),
        createdAt: moment(self.createdAt).format('LL'),
        updatedAt: moment(self.updatedAt).format('LL'),
      };
    },
    get hasChanges() {
      return self.dataHasChanges || self.editorHasChanges;
    },
  }));

const PagesStore = types
  .model('PagesStore', {
    pages: types.array(Page),
    currentPageId: types.maybeNull(types.string),
    tags: types.optional(types.array(types.string), []),
  })
  .actions(self => {
    const updatePage = page => {
      const index = self.pages.findIndex(p => p.uuid === page.uuid);
      if (index > -1) {
        self.pages[index].update(page);
      } else {
        self.pages.push(Page.create(page));
      }
    };

    const updatePageData = (uuid, attr, value) => {
      const page = self.getPage(uuid);
      page.updatePageData({ [attr]: value });
    };

    const updateEntityData = (uuid, blockKey, entityKey, entityData) => {
      const page = self.getPage(uuid);
      const editorState = page.updateEntityData(
        blockKey,
        entityKey,
        entityData,
      );
      page.editorState = editorState;
      self.updatePage({
        uuid,
        editorState,
      });
    };

    const loadAllPages = flow(function*() {
      getStore('appStore').setIsLoading(true);
      const response = yield getPages();
      handleResponse([
        [
          200,
          () => {
            const new_pages = response.data.filter(
              p => self.pages.findIndex(page => page.uuid === p.uuid) === -1,
            );
            self.pages = [...new_pages.map(p => Page.create(p)), ...self.pages];
          },
        ],
        [
          'default',
          () => {
            console.log('error loading pages');
          },
        ],
      ])(response);
      getStore('appStore').setIsLoading(false);
      return response;
    });

    const addPageToCreate = template => {
      console.log(template);
      const page = {
        ...template,
        uuid: `create-new-${template.type}`,
        title: '',
        state: 'draft',
      };
      updatePage(page);
      self.currentPageId = page.uuid;
      return page;
    };

    const loadPage = flow(function*(uuid, setCurrent = false) {
      getStore('appStore').setIsLoading(true);
      const response = yield getPageById(uuid);
      handleResponse([
        [
          200,
          () => {
            updatePage(response.data);
          },
        ],
        [
          'default',
          () => {
            console.log('error loading page', uuid);
          },
        ],
      ])(response);
      getStore('appStore').setIsLoading(false);
      if (setCurrent) {
        self.currentPageId = response.data.uuid;
      }
      return response;
    });

    const loadAllTags = flow(function*() {
      const response = yield getTags();
      handleResponse([
        [
          200,
          () => {
            self.tags = response.data.sort();
          },
        ],
        [
          'default',
          () => {
            console.log('error loading tags');
          },
        ],
      ])(response);
      return response;
    });

    const setCurrentPageId = uuid => {
      self.currentPageId = uuid;
    };

    const deletePage = flow(function*(uuid) {
      const response = yield deletePageById(uuid);
      handleResponse([
        [
          200,
          () => {
            self.pages = self.pages.filter(p => p.uuid !== uuid);
          },
        ],
        [
          'default',
          () => {
            console.log('error deleting page');
          },
        ],
      ])(response);
      return response;
    });

    return {
      addPageToCreate,
      loadAllTags,
      updatePage,
      loadAllPages,
      loadPage,
      updatePageData,
      updateEntityData,
      setCurrentPageId,
      deletePage,
    };
  })
  .views(self => ({
    getPage: uuid => {
      const index = self.pages.findIndex(p => p.uuid === uuid);
      return self.pages[index];
    },
    get currentPage() {
      return self.getPage(self.currentPageId);
    },
  }));

export { PagesStore };
